/**
 * What the evidence compiler found about ONE claim, printed on that claim's card.
 *
 * The compiler in `core/epistemic.ts` has always run — a run reads its stopping
 * rule, and the research workspace prints its whole output — but per-claim it
 * was only ever visible inside the Lab, three screens from the claim it is
 * about. The two findings that matter most on the card are the ones about the
 * label: `unearned-label`, where a claim shows a status stronger than its
 * records earned, and `status-understates-earned`, where it shows a weaker one.
 * A person reading a claim is exactly who needs to know that.
 *
 * The compilation is over the WHOLE session and is not cheap, so it is memoised
 * on the session's node identity, the way `standingMap` and the coach's cue
 * assembly are: one compile per document change, however many cards render.
 * It is behind the instruments switch (`State.instruments`) because it is an
 * instrument — a diagnostic that names a rule and an evidence id is written for
 * somebody auditing the graph, not for somebody reading it.
 *
 * Nothing here calls a model or offers a press that spends.
 */

import { useMemo } from 'react'
import { compileSessionResearch, coverageTracesByOpId } from '../core/researchProjection'
import {
  DEFAULT_EVIDENCE_PROFILE,
  normalizeEvidenceProfile,
  type EpistemicDiagnostic,
} from '../core/epistemic'
import { useStore } from '../core/store'
import type { ReasonNode, Session } from '../core/types'

/**
 * One compile per document, keyed on BOTH fields the compiler reads.
 *
 * AGENTS.md's rule for a derived map is to key it on the identity of the field
 * a write would swap — and this one reads two: the graph, and the research
 * workspace that supplies the evidence profile, the claim annotations and the
 * manual bindings. `setResearchState` swaps `research` while leaving `nodes`
 * exactly where it was, so a key on the graph alone kept serving findings
 * computed under the previous policy until an unrelated node edit happened to
 * displace them. `research` being `undefined` on an exploration that has none
 * compares fine: it is one identity like any other.
 */
let cache: {
  nodes: Session['nodes']
  research: Session['research']
  byNode: Map<string, EpistemicDiagnostic[]>
} | null = null

function diagnosticsFor(session: Session): Map<string, EpistemicDiagnostic[]> {
  if (cache && cache.nodes === session.nodes && cache.research === session.research) {
    return cache.byNode
  }
  const research = session.research
  const { compilation } = compileSessionResearch({
    session,
    // The exploration's own constitution, or what the app ships — the same
    // resolution `startRun` makes, so a diagnostic on the card is the one the
    // run's stopping rule reads and not a second reading of the same records.
    profile: normalizeEvidenceProfile(
      session.research?.evidenceProfile ?? DEFAULT_EVIDENCE_PROFILE,
    ),
    ...(research
      ? {
          overrides: research.genealogyOverrides,
          extraRecords: research.evidenceRecords,
          annotations: research.evidenceAnnotations,
          manualEvidenceBindings: research.manualEvidenceBindings,
          claimAnnotations: research.claimAnnotations,
          decisionCriterionSignals: research.decisionCriterionSignals,
          auditReceipts: research.auditReceipts,
          coverageByOpId: coverageTracesByOpId(research.coverageTraces),
        }
      : {}),
  })
  const byNode = new Map<string, EpistemicDiagnostic[]>()
  for (const claim of compilation.claims) {
    if (!claim.diagnostics.length) continue
    byNode.set(claim.nodeId, claim.diagnostics)
  }
  cache = { nodes: session.nodes, research: session.research, byNode }
  return byNode
}

const TONE: Record<EpistemicDiagnostic['severity'], string> = {
  error: 'var(--ink-tension)',
  warning: 'var(--ink-gap)',
  note: 'var(--text-3)',
}

export function DiagnosticsBlock({ node }: { node: ReasonNode }) {
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  const rows = useMemo(() => diagnosticsFor(session).get(node.id) ?? [], [session, node.id, rev])
  if (node.parentId === null || !rows.length) return null
  return (
    <div className="group" style={{ gap: 'var(--s2)' }}>
      <div className="tierline">
        <span className="eyebrow">What the compiler found</span>
        <span className="vp-spent">
          {rows.length} finding{rows.length === 1 ? '' : 's'}
        </span>
      </div>
      {rows.map((d, i) => (
        <div key={`${d.code}:${i}`} className="group" style={{ gap: 2 }}>
          <p className="gnote" style={{ color: TONE[d.severity] }}>
            <b>{d.code}</b> — {d.message}
          </p>
          {/* The rule, verbatim. A finding that cannot say which rule produced
              it is a verdict, and this whole layer exists to be the opposite. */}
          <p className="gnote gnote--quiet">{d.rule}</p>
        </div>
      ))}
    </div>
  )
}
